import { FC, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBookingById, useCancelBooking } from '../hooks/useBookings';
import { Layout } from '../components/layout';
import { RescheduleModal } from '../components/Dashboard/RescheduleModal';
import { BookingStatus } from '../types/booking.types';
import { formatCurrencyAmount } from '../utils/currency';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format time for display
 */
function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Status badge styles and labels
 */
const statusConfig: Record<BookingStatus, { label: string; className: string }> = {
  PENDING_PAYMENT: { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800' },
  CONFIRMED: { label: 'Confirmed', className: 'bg-green-100 text-green-800' },
  COMPLETED: { label: 'Completed', className: 'bg-blue-100 text-blue-800' },
  CANCELLED: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  NO_SHOW: { label: 'No Show', className: 'bg-gray-100 text-gray-800' },
};

/**
 * Status badge component
 */
const StatusBadge: FC<{ status: BookingStatus }> = ({ status }) => {
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.className}`}>
      {config.label}
    </span>
  );
};

/**
 * Info row component for consistent layout
 */
const InfoRow: FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-3 border-b border-gray-100 dark:border-slate-700 last:border-0">
    <span className="text-gray-600 dark:text-slate-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-slate-100 text-right">{value}</span>
  </div>
);

/**
 * BookingDetailPage - View single booking details
 */
export const BookingDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: booking, isLoading, error } = useBookingById(id || '');
  const cancelBookingMutation = useCancelBooking();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // Check if booking can be cancelled
  const canCancel =
    booking &&
    new Date(booking.startTime) > new Date() &&
    booking.status !== 'CANCELLED' &&
    booking.status !== 'COMPLETED';

  // Check if booking can be rescheduled (only confirmed bookings in the future)
  const canReschedule =
    booking &&
    new Date(booking.startTime) > new Date() &&
    booking.status === 'CONFIRMED';

  // Check if booking needs payment
  const needsPayment = booking?.status === 'PENDING_PAYMENT';

  // Handle reschedule success - refetch booking data
  const handleRescheduleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['booking', id] });
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
    setShowRescheduleModal(false);
  };

  // Handle cancel booking
  const handleCancel = async () => {
    if (!id) return;

    try {
      await cancelBookingMutation.mutateAsync(id);
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('Failed to cancel booking:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-slate-400">Loading booking details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !booking) {
    return (
      <Layout>
        <Helmet>
          <title>Booking Not Found | Massatherapy</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Booking Not Found</h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {error instanceof Error
                ? error.message
                : 'The booking you are looking for does not exist or you do not have permission to view it.'}
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Booking Details | Massatherapy</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link
            to="/dashboard"
            className="inline-flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 mb-6"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>

          {/* Main Card */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-8 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold mb-2">
                    {booking.service?.name || 'Massage Session'}
                  </h1>
                  <p className="text-primary-100">
                    with {booking.therapist?.user?.name || 'Therapist'}
                  </p>
                </div>
                <StatusBadge status={booking.status} />
              </div>
            </div>

            {/* Booking Details */}
            <div className="p-6">
              {/* Date & Time Section */}
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Appointment Details
                </h2>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                  <InfoRow label="Date" value={formatDate(booking.startTime)} />
                  <InfoRow
                    label="Time"
                    value={`${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`}
                  />
                  <InfoRow label="Duration" value={`${booking.service?.duration || 60} minutes`} />
                </div>
              </div>

              {/* Payment Section */}
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Payment Information
                </h2>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                  <InfoRow
                    label="Total Amount"
                    value={formatCurrencyAmount(Number(booking.totalAmount))}
                  />
                  <InfoRow label="Paid Amount" value={formatCurrencyAmount(Number(booking.paidAmount))} />
                  {booking.depositAmount && (
                    <InfoRow
                      label="Deposit"
                      value={formatCurrencyAmount(Number(booking.depositAmount))}
                    />
                  )}
                  <InfoRow
                    label="Payment Status"
                    value={
                      Number(booking.paidAmount) >= Number(booking.totalAmount) ? (
                        <span className="text-green-600">Paid in Full</span>
                      ) : Number(booking.paidAmount) > 0 ? (
                        <span className="text-yellow-600">Partial Payment</span>
                      ) : (
                        <span className="text-red-600">Unpaid</span>
                      )
                    }
                  />
                </div>
              </div>

              {/* Notes Section */}
              {booking.customerNotes && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    Your Notes
                  </h2>
                  <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <p className="text-gray-700 dark:text-slate-300 italic">"{booking.customerNotes}"</p>
                  </div>
                </div>
              )}

              {/* Booking ID */}
              <div className="text-xs text-gray-400 dark:text-slate-500 mb-6">Booking ID: {booking.id}</div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                {needsPayment && (
                  <Link
                    to={`/payment/${booking.id}`}
                    className="flex-1 text-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Complete Payment
                  </Link>
                )}

                {canCancel && (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="flex-1 px-6 py-3 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Cancel Booking
                  </button>
                )}

                {canReschedule && (
                  <button
                    onClick={() => setShowRescheduleModal(true)}
                    className="flex-1 px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    Reschedule
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">Need Help?</h2>
            <p className="text-gray-600 dark:text-slate-400 text-sm mb-4">
              If you have any questions about your booking or need to make changes, our support team
              is here to help.
            </p>
            <Link
              to="/contact"
              className="text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">Cancel Booking?</h3>
              <p className="text-gray-600 dark:text-slate-400 text-sm">
                Are you sure you want to cancel this booking? This action cannot be undone.
                {Number(booking.paidAmount) > 0 && (
                  <span className="block mt-2 text-yellow-600">
                    Note: Refunds may take 5-7 business days to process.
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelBookingMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelBookingMutation.isPending ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
            {cancelBookingMutation.isError && (
              <p className="mt-4 text-sm text-red-600 text-center">
                Failed to cancel booking. Please try again.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {booking && canReschedule && (
        <RescheduleModal
          booking={booking}
          isOpen={showRescheduleModal}
          onClose={() => setShowRescheduleModal(false)}
          onSuccess={handleRescheduleSuccess}
        />
      )}
    </Layout>
  );
};

BookingDetailPage.displayName = 'BookingDetailPage';

export default BookingDetailPage;
